import { useSession } from 'next-auth/react';
import { setCookie } from 'nookies';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { wishListProductTracking } from '@event-tracks/product-analytics';
import { loginFlowSessionStorage } from '@helpers/analytics/loginFlowSessionStorage';
import { getProductsFromAlgolia } from '@services/algolia';
import {
  fetchAddProductToWishlist,
  fetchCreateWishlist,
  fetchGetDefaultWishlist,
  fetchRemoveProductFromWishlist,
} from '@services/customer/wishlist';
import type { TProduct } from 'types/product';
import type { IWishlistAddProduct } from 'types/wishlist';

import SnackBar from '@app/common/cavilha-v2/SnackBar';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons/faCircleCheck';
import { faCircleXmark } from '@fortawesome/pro-regular-svg-icons/faCircleXmark';
import { Icon } from '@madeiramadeirabr/icon-web';
import {
  WISHLIST_ITEMS_PER_PAGE,
  WISHLIST_ORDER_OPTIONS,
  getTotalPages,
  handleProductsInPage,
} from './wishlistContext.consts';
import {
  IAddProductOptions,
  IOrderBy,
  IWishlistContext,
  WishlistProviderProps,
} from './wishlistContext.types';

export const WishlistContext = createContext<IWishlistContext>({} as IWishlistContext);

export function WishlistProvider({ children }: Readonly<WishlistProviderProps>): JSX.Element {
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [wishlistId, setWishlistId] = useState('');
  const [productsInThePage, setProductInThePage] = useState<TProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [allWishlistProducts, setAllWishlistProducts] = useState<TProduct[]>([]);
  const [wProducts, setWProducts] = useState<TProduct[]>([]);
  const [orderBy, setOrderBy] = useState<IOrderBy>('orderBy');
  const [search, setSearch] = useState('');
  const [typeOfEmpty, setTypeOfEmpty] = useState<'empty' | 'notInTheSearch'>('empty');

  const { status } = useSession();

  const addProduct = useCallback(
    async (
      productId: string,
      options?: IAddProductOptions,
      indexName?: string
    ): Promise<IWishlistAddProduct> => {
      try {
        setLoading(true);

        const product = await getProductsFromAlgolia([productId]);
        const validationAttempt = sessionStorage.getItem('wishlistAttempt');

        if (!validationAttempt) {
          wishListProductTracking('AddWishlistAttempt', indexName, product);
        }

        if (status === 'unauthenticated') {
          loginFlowSessionStorage('Favoritos');
          setCookie(null, 'wl_product', `${productId}`, {
            path: '/',
            domain: '.madeiramadeira.com.br',
          });

          if (validationAttempt) {
            wishListProductTracking('AddWishlistAttempt', indexName, product);
          }

          window.location.href = `${window.location.origin}/verificar?url=${options.productURL}`;

          return { products: [] } as IWishlistAddProduct;
        }

        let wishlistToAdd = wishlistId;

        if (!wishlistId) {
          const defaultWishlist = await fetchGetDefaultWishlist();

          if (!defaultWishlist) {
            const { success, data: newWishlist } = await fetchCreateWishlist();

            if (!success) {
              throw new Error('Falha ao criar wishlist default');
            }

            setWishlistId(newWishlist.id);
            wishlistToAdd = newWishlist.id;
          }
        }

        const { success, data: addToWishlist } = await fetchAddProductToWishlist({
          wishlistId: wishlistToAdd,
          productId,
        });

        if (!success || !addToWishlist.products.includes(productId)) {
          SnackBar.show({
            icon: <Icon icon={faCircleXmark} size="icon-medium" />,
            message:
              'Erro ao adicionar o seu produto a lista de favoritos. Tente novamente mais tarde.',
            type: 'danger',
            timeout: 5000,
            showCloseButton: true,
          });

          return { products: [] } as IWishlistAddProduct;
        }

        const newWishlistProducts = await getProductsFromAlgolia(addToWishlist.products);
        setAllWishlistProducts(newWishlistProducts);

        SnackBar.show({
          icon: <Icon icon={faCircleCheck} size="icon-medium" />,
          message:
            'Produto adicionado! <a href="/lista-de-favoritos">Veja na sua lista de favoritos</a>',
          type: 'success',
          timeout: 5000,
          showCloseButton: true,
        });
        wishListProductTracking('AddToWishlist', indexName, product);

        return addToWishlist;
      } catch (err) {
        SnackBar.show({
          icon: <Icon icon={faCircleXmark} size="icon-medium" />,
          message:
            'Erro ao adicionar o seu produto a lista de favoritos. Tente novamente mais tarde.',
          type: 'danger',
          timeout: 5000,
          showCloseButton: true,
        });

        return { products: [] } as IWishlistAddProduct;
      } finally {
        setLoading(false);
      }
    },
    [status, wishlistId]
  );

  const updateWishlist = (page: number, products: TProduct[]) => {
    setProductInThePage(handleProductsInPage(WISHLIST_ITEMS_PER_PAGE, page, products));
    setTotalPages(getTotalPages(WISHLIST_ITEMS_PER_PAGE, products));
  };

  const removeProduct = useCallback(
    async (productId: string, indexName?: string): Promise<TProduct[]> => {
      try {
        setLoading(true);

        const product = await getProductsFromAlgolia([productId]);
        const { success, data } = await fetchRemoveProductFromWishlist({
          wishlistId,
          productId,
        });

        wishListProductTracking('RemoveFromWishlist', indexName, product);
        if (!success) {
          return [];
        }

        SnackBar.show({
          icon: <Icon icon={faCircleCheck} size="icon-medium" />,
          message: 'Produto removido com sucesso',
          type: 'success',
          timeout: 5000,
          showCloseButton: true,
        });

        return await getProductsFromAlgolia(data.products);
      } catch {
        SnackBar.show({
          icon: <Icon icon={faCircleXmark} size="icon-medium" />,
          message:
            'Erro ao remover o seu produto a lista de favoritos. Tente novamente mais tarde.',
          type: 'danger',
          timeout: 5000,
          showCloseButton: true,
        });
        return [];
      } finally {
        setLoading(false);
      }
    },
    [wishlistId]
  );

  const getWishlistProducts = useCallback(
    async (searching = false): Promise<void> => {
      try {
        setLoading(true);

        if (orderBy !== 'orderBy' && !searching) {
          return;
        }

        const defaultWishlist = await fetchGetDefaultWishlist();
        if (!defaultWishlist) {
          return;
        }
        const { products: productsIds, id } = defaultWishlist;
        setWishlistId(id);

        if (!productsIds.length) {
          return;
        }

        const products = await getProductsFromAlgolia(productsIds);

        setAllWishlistProducts(products);
        setWProducts(products);
        setProductInThePage(handleProductsInPage(WISHLIST_ITEMS_PER_PAGE, currentPage, products));
        setTotalPages(getTotalPages(WISHLIST_ITEMS_PER_PAGE, products));
      } finally {
        setLoading(false);
      }
    },
    [currentPage, orderBy]
  );

  const orderProductsBy = useCallback(
    async (order: IOrderBy) => {
      setLoading(true);
      setOrderBy(order);

      const orderedProducts = WISHLIST_ORDER_OPTIONS?.[order](allWishlistProducts) || [];

      setAllWishlistProducts(orderedProducts);
      setProductInThePage(handleProductsInPage(WISHLIST_ITEMS_PER_PAGE, 1, orderedProducts));
      setLoading(false);
      setCurrentPage(1);
    },
    [allWishlistProducts]
  );

  const searchProductsFor = useCallback(
    async (value: string) => {
      const regexOnlyNumbers = /^[^a-z]\d*$/gm;
      setSearch(value);

      if (!value.length || value.length === 0) {
        setTypeOfEmpty('empty');
        await getWishlistProducts(true);
        return;
      }

      if (value.length <= 3) {
        return;
      }

      setLoading(true);

      const searchedProducts = regexOnlyNumbers.test(value)
        ? wProducts.filter(product => product.id.toString().search(value) !== -1)
        : wProducts.filter(
            product =>
              product.name
                .replace('-', ' ')
                .normalize('NFD')
                .toLowerCase()
                .search(value.normalize('NFD').toLowerCase()) !== -1
          );

      if (!searchedProducts.length) {
        setTypeOfEmpty('notInTheSearch');
      }

      setTotalPages(getTotalPages(WISHLIST_ITEMS_PER_PAGE, searchedProducts));
      setAllWishlistProducts(searchedProducts);
      setCurrentPage(1);
      setProductInThePage(handleProductsInPage(WISHLIST_ITEMS_PER_PAGE, 1, searchedProducts));
      setLoading(false);
    },
    [getWishlistProducts, wProducts]
  );

  useEffect(() => {
    const action = async () => {
      if (status !== 'authenticated') {
        return;
      }

      try {
        await getWishlistProducts();
      } catch (err) {
        SnackBar.show({
          icon: <Icon icon={faCircleXmark} size="icon-medium" />,
          message:
            'Erro ao consultar os produtos da sua lista de favoritos. Tente novamente mais tarde.',
          type: 'danger',
          timeout: 5000,
          showCloseButton: true,
        });
      }
    };

    action();
  }, [getWishlistProducts, status]);

  const contextValue: IWishlistContext = useMemo(
    () => ({
      totalPages,
      currentPage,
      setCurrentPage,
      wishlistId,
      productsInThePage,
      loading,
      setLoading,
      allWishlistProducts,
      orderBy,
      search,
      typeOfEmpty,
      addProduct,
      removeProduct,
      orderProductsBy,
      searchProductsFor,
      getWishlistProducts,
      updateWishlist,
    }),
    [
      addProduct,
      allWishlistProducts,
      currentPage,
      getWishlistProducts,
      searchProductsFor,
      loading,
      orderBy,
      orderProductsBy,
      productsInThePage,
      removeProduct,
      search,
      totalPages,
      typeOfEmpty,
      wishlistId,
    ]
  );

  return <WishlistContext.Provider value={contextValue}>{children}</WishlistContext.Provider>;
}

export const useWishlist = (): IWishlistContext => useContext(WishlistContext);
